/**
 * Format a `Date` per a PHP-style date format string, honoring the token ORDER
 * and the literal SEPARATORS the staff member chose.
 *
 * `new Intl.DateTimeFormat(locale, options).format()` renders a date in the
 * LOCALE's canonical order/separator and ignores both the requested property
 * order and any separator. So `Y-m-d`, `Y/m/d`, `d/m/Y`, `d-m-Y`, `d.m.Y` all
 * collapse to one identical locale-default string, which makes the distinct
 * staff Settings date-format options meaningless (a known `Intl` limitation).
 *
 * This rebuilds the output from `formatToParts()`: the component STYLES stay
 * locale-aware (localized month names, locale digits) while the chosen token
 * order and separators are applied faithfully.
 *
 * Supported tokens: `Y` (year), `m` (numeric month), `M` (short month name),
 * `F` (long month name), `d` (day). Every other character is emitted literally
 * (`-`, `/`, `.`, `,`, space). Day padding follows the prior convention:
 * 2-digit next to a numeric month, non-padded next to a named month.
 */
export function formatPhpDate(
  date: Date,
  phpFormat: string,
  locale: string,
  timeZone?: string,
): string {
  const monthStyle: 'long' | 'short' | '2-digit' = phpFormat.includes('F')
    ? 'long'
    : phpFormat.includes('M')
      ? 'short'
      : '2-digit';

  const options: Intl.DateTimeFormatOptions = {};
  if (phpFormat.includes('Y')) {
    options.year = 'numeric';
  }
  if (phpFormat.includes('m') || phpFormat.includes('M') || phpFormat.includes('F')) {
    options.month = monthStyle;
  }
  if (phpFormat.includes('d')) {
    options.day = monthStyle === '2-digit' ? '2-digit' : 'numeric';
  }
  if (timeZone) {
    options.timeZone = timeZone;
  }

  const parts = new Intl.DateTimeFormat(locale, options).formatToParts(date);
  const part: Record<'year' | 'month' | 'day', string> = { year: '', month: '', day: '' };
  for (const p of parts) {
    if (p.type === 'year' || p.type === 'month' || p.type === 'day') {
      part[p.type] = p.value;
    }
  }

  let out = '';
  for (const ch of phpFormat) {
    if (ch === 'Y') {
      out += part.year;
    } else if (ch === 'm' || ch === 'M' || ch === 'F') {
      out += part.month;
    } else if (ch === 'd') {
      out += part.day;
    } else {
      out += ch;
    }
  }
  return out;
}
